/**
 * P4 follow-up (plan sleepy-wiggling-crown): resolve paywall product ids to
 * display names from the SCREEN CONFIG the editor already holds — the
 * warehouse `paywall_offer_dim` carries no product name and the events carry
 * ids only, so the client is the single place that knows what «OBW5…zUmL»
 * is called. Best-effort by design: unknown shapes resolve to nothing and
 * the outcomes section falls back to shortened ids.
 *
 * Branch structure mirrors the server's
 * FunnelAnalyticsMetadataService.extractOfferCandidates so the two never
 * disagree about WHERE products live in a screen config.
 */

type JsonRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const asRecord = (value: unknown): JsonRecord => (isRecord(value) ? value : {});
const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

/** LocalizedLabel → display text: plain string, {text}, or translations map. */
function localizedText(label: unknown): string | undefined {
  if (typeof label === 'string') return asString(label);
  if (!isRecord(label)) return undefined;
  const direct = asString(label.text);
  if (direct) return direct;
  const translations = asRecord(label.translations);
  return asString(translations.en) ?? Object.values(translations).map(asString).find(Boolean);
}

function productLabel(product: JsonRecord): string | undefined {
  return (
    asString(product.name) ??
    asString(product.title) ??
    asString(product.label) ??
    asString(product.displayName) ??
    localizedText(product.topStartLabel) ??
    localizedText(product.purchaseLabel)
  );
}

function productIdOf(product: JsonRecord): string | undefined {
  return asString(product.productId) ?? asString(product.id);
}

function collect(products: unknown[], into: Record<string, string>): void {
  for (const raw of products) {
    if (!isRecord(raw)) continue;
    const id = productIdOf(raw);
    const label = productLabel(raw);
    if (id && label && !(id in into)) into[id] = label;
  }
}

function collectCatalogSections(sections: unknown[], into: Record<string, string>): void {
  for (const section of sections) {
    if (!isRecord(section) || section.kind !== 'ProductCatalog') continue;
    collect(asArray(asRecord(section.productCatalogContent).items), into);
  }
}

export function extractPaywallProductLabels(screen: unknown): Record<string, string> {
  const labels: Record<string, string> = {};
  if (!isRecord(screen)) return labels;
  const content = asRecord(screen.content);
  const screenType = asString(screen.screenType);

  if (screenType === 'Paywall') {
    collect(asArray(asRecord(asRecord(content.paywall).products).items), labels);
  }
  if (screenType === 'FlexibleLayout') {
    const direct = asArray(content.sections).filter(isRecord);
    const sections = direct.length > 0 ? direct : asArray(asRecord(content.flexibleLayout).sections);
    collectCatalogSections(sections, labels);
    collectCatalogSections(
      asArray(asRecord(asRecord(content.flexibleLayout).stickyContainer).sections),
      labels,
    );
  }
  if (screenType === 'WebEmbed') {
    const embed = asRecord(content.embed);
    collect(asArray(embed.purchaseProducts), labels);
    collectCatalogSections(asArray(embed.childSections), labels);
  }
  return labels;
}
